import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectSystemTags } from "@/store/tags/tags-selectors";
import { Tag } from "@/store/tags/tags-slice";
import { selectCurrentUser } from "@/store/user-selectors";
import { generateGUID } from "@faro-lotv/foundation";
import { ILabel, LabelType, MarkupIElement } from "@faro-lotv/ielement-types";
import { selectRootId } from "@faro-lotv/project-source";
import {
  createMutationAddAllowedLabel,
  createMutationAddLabel,
  createMutationSetElementMetaData,
} from "@faro-lotv/service-wires";
import { useCallback, useMemo, useRef, useState } from "react";

type ApproveReject = {
  approve(markup: MarkupIElement, notes?: string): Promise<Tag | undefined>;
  reject(markup: MarkupIElement, notes?: string): Promise<Tag | undefined>;
  isRunning: boolean;
};

/**
 * Hook to approve/reject AI markups by adding labels, optionally with review notes metadata
 *
 * @returns functions to approve/reject a markup
 */
export function useApproveRejectAiMarkup(): ApproveReject {
  const dispatch = useAppDispatch();
  const projectApiClient = useCurrentProjectApiClient();
  const systemTags = useAppSelector(selectSystemTags);
  const rootId = useAppSelector(selectRootId);
  const { handleErrorWithToast } = useErrorHandlers();
  const currentUser = useAppSelector(selectCurrentUser);
  const pendingLabelCreation = useRef<Map<string, Promise<Tag>>>(new Map());
  const [isRunning, setIsRunning] = useState(false);

  // Build a lookup map for AI review status labels
  const reviewStatusLabels = useMemo(() => {
    const labelMap = new Map<string, Tag>();
    systemTags.forEach((tag) => {
      if (tag.labelType === LabelType.AiReviewStatus) {
        labelMap.set(tag.name.toLowerCase(), tag);
      }
    });
    return labelMap;
  }, [systemTags]);

  const getOrCreateLabel = useCallback(
    async (labelName: string) => {
      const normalizedName = labelName.toLowerCase();
      const existingLabel = reviewStatusLabels.get(normalizedName);
      if (existingLabel) return existingLabel;

      // Avoid duplicate label creation requests
      const ongoingCreation = pendingLabelCreation.current.get(normalizedName);
      if (ongoingCreation) return ongoingCreation;

      const labelCreationTask = (async () => {
        const labelData: ILabel = {
          id: generateGUID(),
          name: labelName,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id ?? "",
          resourceId: rootId ?? "",
          labelType: LabelType.AiReviewStatus,
          lastModifiedInDb: new Date().toISOString(),
        };
        const mutation = createMutationAddAllowedLabel(labelData);

        try {
          await projectApiClient.applyMutations([mutation]);

          const newTag: Tag = {
            id: mutation.label.id,
            name: mutation.label.name,
            labelType: LabelType.AiReviewStatus,
          };
          // Update our local cache so future lookups are faster
          reviewStatusLabels.set(normalizedName, newTag);

          return newTag;
        } catch (error) {
          handleErrorWithToast({ title: "Failed to create label", error });
          throw error;
        } finally {
          pendingLabelCreation.current.delete(normalizedName);
        }
      })();

      pendingLabelCreation.current.set(normalizedName, labelCreationTask);
      return await labelCreationTask;
    },
    [
      reviewStatusLabels,
      projectApiClient,
      rootId,
      handleErrorWithToast,
      currentUser?.id,
    ],
  );

  const applyLabelAndNotes = useCallback(
    async (markup: MarkupIElement, labelId: string, reviewNotes?: string) => {
      const labelMutation = createMutationAddLabel(markup.id, labelId);
      const mutations = reviewNotes?.trim()
        ? [
            labelMutation,
            createMutationSetElementMetaData(markup.id, [
              { key: "reviewNotes", value: reviewNotes, skipIfPresent: false },
            ]),
          ]
        : [labelMutation];

      await projectApiClient.applyMutations(mutations);
      // Refresh the project data to reflect the changes
      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: { ancestorIds: [markup.id] },
        }),
      );
    },
    [dispatch, projectApiClient],
  );

  const approve = useCallback(
    async (
      markup: MarkupIElement,
      notes?: string,
    ): Promise<Tag | undefined> => {
      if (isRunning) return;

      setIsRunning(true);
      try {
        const approvedLabel = await getOrCreateLabel("Approved");
        await applyLabelAndNotes(markup, approvedLabel.id, notes);
        return approvedLabel;
      } finally {
        setIsRunning(false);
      }
    },
    [applyLabelAndNotes, getOrCreateLabel, isRunning],
  );

  const reject = useCallback(
    async (
      markup: MarkupIElement,
      notes?: string,
    ): Promise<Tag | undefined> => {
      if (isRunning) return;

      setIsRunning(true);
      try {
        const rejectedLabel = await getOrCreateLabel("Rejected");
        await applyLabelAndNotes(markup, rejectedLabel.id, notes);
        return rejectedLabel;
      } finally {
        setIsRunning(false);
      }
    },
    [applyLabelAndNotes, getOrCreateLabel, isRunning],
  );

  return {
    approve,
    reject,
    isRunning,
  } as const;
}
